import { ProcessedHistory, groupHistoryByWorkflow } from '../../ContactManager.utils';
import { AudioModal, TranscriptModal } from 'components';
import { Accordion } from 'components/Accordion';
import { CallInfo } from 'types/agentCommandCenter.type';
import { useMemo, useState, useEffect } from 'react';
import { useAccordion } from 'hooks';

import styles from '../../ContactManager.module.scss';
import { SMSItem } from './SMSItem';
import { CallItem } from './CallItem';
import { useGetCallDispositions } from 'services/hooks/queries/useGetCallDispositions';
import { usePostBatchQualifications, usePostBatchAggregatedQualifications } from 'services/hooks/queries';
import { useAuth } from 'contexts/AuthContext';
import { hasAnyRole, UserRole } from 'types/userRoles';
import { formatDisposition } from 'pages/AgenticWorkflowDetail/utils';
import { formatStatusForDisplay } from 'types/contact.type';

export const TabDetails = ({
    contactHistory,
    workflowPartyStages = {},
    contactName = '',
}: {
    contactHistory: ProcessedHistory[];
    workflowPartyStages?: Record<string, string>;
    contactName?: string;
}) => {
    const [showTranscriptModal, setShowTranscriptModal] = useState(false);
    const [showAudioModal, setShowAudioModal] = useState(false);

    const [currentCallId, setCurrentCallId] = useState<string | null>(null);
    const [currentCallInfo, setCurrentCallInfo] = useState<CallInfo | undefined>(undefined);
    const { callDispositions } = useGetCallDispositions();
    const { userRoles } = useAuth();
    const hasManagerRole = hasAnyRole(userRoles, [UserRole.MANAGER, UserRole.SUPER_ADMIN, UserRole.ADMIN]);

    const callHistoryItems = useMemo(
        () =>
            contactHistory.filter((item): item is Extract<ProcessedHistory, { type: 'call' }> => item.type === 'call'),
        [contactHistory]
    );

    const callIds = useMemo(() => callHistoryItems.map((historyItem) => historyItem.id), [callHistoryItems]);

    const workflowHistoryGroups = useMemo(
        () => groupHistoryByWorkflow(contactHistory, workflowPartyStages),
        [contactHistory, workflowPartyStages]
    );
    const firstWorkflowId = workflowHistoryGroups[0]?.workflowId;

    const accordion = useAccordion({
        allowMultiple: true,
        defaultOpen: firstWorkflowId ? [firstWorkflowId] : [],
    });

    useEffect(() => {
        if (firstWorkflowId) {
            accordion.closeAll();
            accordion.openSection(firstWorkflowId);
        } else {
            accordion.closeAll();
        }
    }, [contactHistory, firstWorkflowId, accordion.closeAll, accordion.openSection]);

    const aggregatedCallIds = useMemo(
        () =>
            callHistoryItems
                .filter((historyItem) => historyItem.aggregatedTagsUsed)
                .map((historyItem) => historyItem.id),
        [callHistoryItems]
    );

    const { batchQualificationsData, isBatchQualificationsLoading } = usePostBatchQualifications(callIds);
    const { batchAggregatedQualificationsData, isBatchAggregatedQualificationsLoading } =
        usePostBatchAggregatedQualifications(aggregatedCallIds);

    const onViewTranscript = (callId: string, callInfo: CallInfo) => {
        setShowTranscriptModal(true);
        setCurrentCallId(callId);
        setCurrentCallInfo(callInfo);
    };

    const onListenRecording = (callId: string, callInfo: CallInfo) => {
        setShowAudioModal(true);
        setCurrentCallId(callId);
        setCurrentCallInfo(callInfo);
    };

    return (
        <div className={styles.callsSection}>
            <h3>Contact History</h3>

            {contactHistory.length > 0 ? (
                <div className={styles.workflowHistoryGroups}>
                    {workflowHistoryGroups.map((group) => (
                        <Accordion
                            key={group.workflowId}
                            className={styles.workflowAccordion}
                            isOpen={accordion.isOpen(group.workflowId)}
                            onToggle={() => accordion.toggleSection(group.workflowId)}
                            header={
                                <div className={styles.workflowAccordionHeader}>
                                    <span className={styles.workflowAccordionTitle}>{group.workflowName}</span>
                                    {group.partyStage && (
                                        <span className={styles.workflowAccordionSubtitle}>
                                            Party Status: {formatStatusForDisplay(group.partyStage)}
                                        </span>
                                    )}
                                    {group.lastDisposition && (
                                        <span className={styles.workflowAccordionSubtitle}>
                                            Disposition: {formatDisposition(group.lastDisposition)}
                                        </span>
                                    )}
                                </div>
                            }
                        >
                            <div className={styles.callsTimeline}>
                                {group.items.map((historyItem) => {
                                    if (historyItem.type === 'sms') {
                                        return <SMSItem key={historyItem.id} {...historyItem} />;
                                    }
                                    if (historyItem.type === 'call') {
                                        return (
                                            <CallItem
                                                key={historyItem.id}
                                                {...historyItem}
                                                onViewTranscript={onViewTranscript}
                                                onListenRecording={onListenRecording}
                                                contactName={contactName}
                                                callDispositions={callDispositions?.dispositions || []}
                                                hasManagerRole={hasManagerRole}
                                                batchQualificationsData={batchQualificationsData}
                                                isBatchQualificationsLoading={isBatchQualificationsLoading}
                                                batchAggregatedQualificationsData={batchAggregatedQualificationsData}
                                                isBatchAggregatedQualificationsLoading={
                                                    isBatchAggregatedQualificationsLoading
                                                }
                                            />
                                        );
                                    }
                                })}
                            </div>
                        </Accordion>
                    ))}
                </div>
            ) : (
                <p className={styles.noCallsMessage}>No call history available for this contact.</p>
            )}

            <TranscriptModal
                isOpen={showTranscriptModal}
                onClose={() => {
                    setShowTranscriptModal(false);
                    setCurrentCallId(null);
                    setCurrentCallInfo(undefined);
                }}
                callId={currentCallId}
                callInfo={currentCallInfo}
            />

            <AudioModal
                isOpen={showAudioModal}
                onClose={() => {
                    setShowAudioModal(false);
                    setCurrentCallId(null);
                    setCurrentCallInfo(undefined);
                }}
                callId={currentCallId}
                callInfo={currentCallInfo}
            />
        </div>
    );
};
